import { useApolloClient } from '@apollo/client';
import _ from 'underscore';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import * as Papa from 'papaparse';
import momentjs from 'moment';

import { Button } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import Modal, {
	ModalBackground,
	ModalContent,
} from 'packages/kakapo-ui-kit/components/modals/Modal';
import ModalHeader, {
	ModalHeaderTitle,
} from 'packages/kakapo-ui-kit/components/modals/ModalHeader';
import { SectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import Paragraph from 'packages/kakapo-ui-kit/components/common/Paragraph';
import ProgressBar from 'packages/kakapo-ui-kit/components/common/ProgressBar';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

const Description = styled(Paragraph)`
	text-align: center;
`;

const Progress = styled(Box)`
	color: ${(props) => props.theme.colors.slateBlue};
`;

export default function InboxManagerCsvExport(props: {
	ids: string[];
	dateRange: [Date, Date];
	onFinish: () => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const [progress, setProgress] = useState(0.0);
	const [url, setUrl] = useState<string | undefined>(undefined);

	const client = useApolloClient();

	const momentTypes: Record<graphql.MomentType, string> = {
		[graphql.MomentType.Evaluation]: 'Evaluation Moment',
		[graphql.MomentType.Feedback]: 'Feedback Moment',
		[graphql.MomentType.Goals]: 'Goal Setting Moment',
		[graphql.MomentType.Message]: 'Message Moment',
		[graphql.MomentType.TalentReview]: 'Talent Review Moment',
	};

	const conversationStatuses: Record<graphql.EvaluationSubmissionStatus, string> = {
		[graphql.EvaluationSubmissionStatus.Draft]: 'Draft',
		[graphql.EvaluationSubmissionStatus.Shared]: 'Shared',
		[graphql.EvaluationSubmissionStatus.Signed]: 'Signed',
	};

	useEffect(() => {
		async function download() {
			const chunks = _.chunk(props.ids, 10);

			const results: graphql.User[] = [];

			for (const chunk of chunks) {
				const response = await client.query<graphql.ManagerInboxCsvExportQuery>({
					query: graphql.ManagerInboxCsvExportDocument,
					variables: {
						ids: chunk,
						startDate: props.dateRange[0],
						endDate: props.dateRange[1],
					},
				});

				results.push(...(response.data.usersByIDs as graphql.User[]));

				setProgress(results.length / props.ids.length);
			}

			function formatPercentage(value: number): string {
				if (value.valueOf() !== value) {
					return '-';
				}

				return (value * 100).toFixed(0) + '%';
			}

			function isMomentCompleted(moment: graphql.MomentInstance) {
				if (moment.status !== graphql.MomentStatus.Completed) {
					return false;
				}

				if (moment.momentTemplate.type === graphql.MomentType.Evaluation) {
					return (
						moment.evaluation?.employee.status &&
						[
							graphql.EvaluationSubmissionStatus.Shared,
							graphql.EvaluationSubmissionStatus.Signed,
						].includes(moment.evaluation?.employee.status)
					);
				}

				if (moment.momentTemplate.type === graphql.MomentType.Feedback) {
					return moment.feedbackRequest?.responses.edges.every(
						({ node }) => node.status === graphql.FeedbackResponseStatus.Shared,
					);
				}

				if (moment.momentTemplate.type === graphql.MomentType.Goals) {
					return moment.goals?.every(({ status }) => status === graphql.GoalStatus.Completed);
				}

				return true;
			}

			const records = results
				.flatMap((user) => user.moments.map((moment) => ({ user, moment })))
				.map(({ user, moment }) => [
					user.username,
					user.firstName,
					user.lastName,
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(localize(user.jobPositions[0]?.jobTitle.title ?? ([] as any)) as any)?.text ?? '',
					user.group.name,
					momentTypes[moment.momentTemplate.type],
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(localize(moment.momentTemplate.title) as any).text,
					momentjs(new Date(moment.startDate)).format('DD-MM-YYYY'),
					momentjs(new Date(moment.endDate)).format('DD-MM-YYYY'),
					isMomentCompleted(moment as graphql.MomentInstance) ? 'Yes' : 'No',
					new Date(moment.endDate) < new Date() ? 'Yes' : 'No',
					moment.evaluation?.manager
						? [
								moment.evaluation.manager.user.firstName,
								moment.evaluation.manager.user.lastName,
						  ].join(' ')
						: '-',
					moment.evaluation ? conversationStatuses[moment.evaluation.manager.status] : '-',
					moment.evaluation ? conversationStatuses[moment.evaluation.employee.status] : '-',
					moment.goals
						? formatPercentage(
								moment.goals
									.map((goal) =>
										goal.status === graphql.GoalStatus.Completed ? 1.0 : goal.progress,
									)
									.reduce((p, c) => p + c, 0.0) / moment.goals.length,
						  )
						: '-',
					moment.feedbackRequest
						? formatPercentage(
								moment.feedbackRequest.responses.edges.filter(
									({ node }) => node.status === graphql.FeedbackResponseStatus.Shared,
								).length / moment.feedbackRequest.responses.edges.length,
						  )
						: '-',
				]);

			const data = Papa.unparse([
				[
					'Username',
					'First Name',
					'Last Name',
					'Job',
					'Department',
					'Moment Type',
					'Moment Name',
					'Moment Start Date',
					'Moment End Date',
					'Moment Completed',
					'Moment Due',
					'Conversation Partner',
					'Conversation Status Manager',
					'Conversation Status Employee',
					'Goal Progress',
					'Feedback Progress',
				],
				...records,
			]);
			const blob = new Blob([data], { type: 'text/csv' });
			setUrl(URL.createObjectURL(blob));
		}

		download();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<ModalBackground>
			<Modal>
				<ModalHeader onCancel={() => props.onFinish()}>
					<ModalHeaderTitle>{t('inbox.manager.csvExportModal.title')}</ModalHeaderTitle>
				</ModalHeader>
				<ModalContent>
					{url ? (
						<Flex alignItems="center" flexDirection="column">
							<SectionTitle>{t('inbox.manager.csvExportModal.success.title')}</SectionTitle>
							<Description color="slateBlue">
								{t('inbox.manager.csvExportModal.success.message')}
							</Description>
							<Box mt={3}>
								<Button.Anchor
									type="primary"
									href={url}
									download={t('inbox.manager.csvExportModal.fileName', {
										date: momentjs().format('DD-MM-YYYY'),
									})}
								>
									{t('inbox.manager.csvExportModal.downloadButton.title')}
								</Button.Anchor>
							</Box>
						</Flex>
					) : (
						<>
							<Spinner isLoading size="18px" />
							<Flex alignItems="center" flexDirection="column">
								<SectionTitle>{t('inbox.manager.csvExportModal.subtitle')}</SectionTitle>
								<Description color="slateBlue">
									{t('inbox.manager.csvExportModal.explanation')}
								</Description>
								<Flex alignItems="center" mt={3}>
									<Box width={200}>
										<ProgressBar progress={progress} />
									</Box>
									<Progress ml={2}>{(progress * 100).toFixed(0)}%</Progress>
								</Flex>
							</Flex>
						</>
					)}
				</ModalContent>
			</Modal>
		</ModalBackground>
	);
}
